import React from 'react';
import { useWatch, type Control, type UseFormSetValue } from 'react-hook-form';
import type {
    StoreDetailsInviteUserFormValues,
    StoreDetailsInviteUserLoginMethod,
    StoreDetailsInviteUserRole,
} from '../../../../types/features/storeDetails/users';
import {
    getDigitsOnly,
    isStoreDetailsInvitePinLoginAllowed,
    shouldShowStoreDetailsRecoPermission,
} from '../utils/storeDetailsInviteUserUtils';

type UseStoreDetailsAddUserDialogLogicParams = {
    control: Control<StoreDetailsInviteUserFormValues>;
    setValue: UseFormSetValue<StoreDetailsInviteUserFormValues>;
    onSubmit: () => void;
};

export const useStoreDetailsAddUserDialogLogic = ({
    control,
    setValue,
    onSubmit,
}: UseStoreDetailsAddUserDialogLogicParams) => {
    const selectedRole = useWatch({ control, name: 'role' });
    const selectedLoginMethod = useWatch({ control, name: 'loginMethod' });
    const pinLoginAllowed = isStoreDetailsInvitePinLoginAllowed(selectedRole);
    const showRecoPermission = shouldShowStoreDetailsRecoPermission(selectedRole);

    const handleFormSubmit = React.useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        onSubmit();
    }, [onSubmit]);

    const handleRoleChange = React.useCallback((nextRole: StoreDetailsInviteUserRole) => {
        setValue('role', nextRole, { shouldDirty: true, shouldValidate: true });
        if (!isStoreDetailsInvitePinLoginAllowed(nextRole) && selectedLoginMethod === 'pin') {
            setValue('loginMethod', 'phone', { shouldDirty: true, shouldValidate: true });
            setValue('cashier_mode_pin', '', { shouldDirty: true, shouldValidate: true });
        }
        if (!shouldShowStoreDetailsRecoPermission(nextRole)) {
            setValue('can_approve_reconciliation', false, { shouldDirty: true, shouldValidate: true });
        }
    }, [selectedLoginMethod, setValue]);

    const handleLoginMethodChange = React.useCallback((nextLoginMethod: StoreDetailsInviteUserLoginMethod) => {
        if (nextLoginMethod === 'pin' && !pinLoginAllowed) {
            return;
        }

        setValue('loginMethod', nextLoginMethod, { shouldDirty: true, shouldValidate: true });
        if (nextLoginMethod === 'pin') {
            setValue('phone_number', '', { shouldDirty: true, shouldValidate: true });
            setValue('email', '', { shouldDirty: true, shouldValidate: true });
            return;
        }

        setValue('cashier_mode_pin', '', { shouldDirty: true, shouldValidate: true });
    }, [pinLoginAllowed, setValue]);

    const handlePhoneChange = React.useCallback((value: string) => {
        setValue('phone_number', getDigitsOnly(value).slice(0, 10), { shouldDirty: true, shouldValidate: true });
    }, [setValue]);

    const handlePinChange = React.useCallback((value: string) => {
        setValue('cashier_mode_pin', getDigitsOnly(value).slice(0, 6), { shouldDirty: true, shouldValidate: true });
    }, [setValue]);

    return {
        handleFormSubmit,
        handleLoginMethodChange,
        handlePhoneChange,
        handlePinChange,
        handleRoleChange,
        pinLoginAllowed,
        selectedLoginMethod,
        selectedRole,
        showRecoPermission,
    };
};
